import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import AlertDialog from "@/shared/components/kit/modal";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Box, CircularProgress, InputAdornment, TextField, useMediaQuery, useTheme } from "@mui/material";
import React, { useState } from "react";
import ContentModal from "./contentModal";
import PaymentIcon from "@mui/icons-material/Payment";
import OtpInput from "@/shared/components/common/otp";
import ImgStaticCard from "@/shared/components/common/imgStaticCard/imgStaticCard";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchWalletBalance, postDetailTransfer, postOtpTransfer, postPreTransfer, } from "@/api/user/home";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import CustomSelectNormal from "@/shared/components/common/select/selectNormal";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import useMaskedCardNumber from "@/hooks/useMaskedCardNumber";
import { fetchProfile } from "@/api/user/profile";

const transferSchema = z.object({
  amount: z.string().min(1, "مبلغ الزامی است"),
  card: z.string().min(1, "شماره کیف پول الزامی است"),
  payFor: z.string().min(1, "شرح الزامی است"),
});
/**
 * ContentBalanceTransfer
 *
 * کامپوننت اصلی انتقال موجودی (کارت به کارت بین کیف پول‌ها).
 *
 * این کامپوننت فرآیند کامل انتقال وجه بین کیف پول‌ها را مدیریت می‌کند، شامل مراحل زیر:
 * 1. ورود اطلاعات انتقال (مبلغ، شماره کیف پول مقصد، شرح)
 * 2. تأیید اطلاعات و نمایش خلاصه تراکنش
 * 3. تأیید نهایی با ورود کد تأیید (OTP)
 *
 * ویژگی‌ها:
 * - اعتبارسنجی ورودی‌ها با استفاده از `zod` و `react-hook-form`
 * - مدیریت مراحل چندگانه (step-based) با استفاده از state محلی
 * - ارتباط با سرور از طریق API‌های `fetchWalletBalance`، `postPreTransfer`، `postDetailTransfer` و `postOtpTransfer`
 * - نمایش اعلان‌ها (toast) برای موفقیت و خطا
 * - استفاده از `react-query` برای مدیریت داده‌ها و mutation‌ها
 * - طراحی کاملاً ریسپانسیو برای موبایل و دسکتاپ با استفاده از `useMediaQuery`
 *
 * بخش‌های اصلی:
 * - فرم ورود اطلاعات تراکنش (مبلغ، شماره کیف پول، شرح)
 * - انتخاب نوع موجودی (نقدی یا اعتباری) با `CustomSelectNormal`
 * - نمایش کارت گرافیکی با `ImgStaticCard`
 * - دو مودال تأیید: خلاصه تراکنش و تأیید OTP
 *
 * کامپوننت‌های داخلی استفاده‌شده:
 * - StackKit
 * - BoxKit
 * - TypoGraphyKit
 * - CustomButton
 * - CustomSelectNormal
 * - ImgStaticCard
 * - AlertDialog
 * - ContentModal
 * - OtpInput
 * - CircularProgress (برای وضعیت بارگذاری)
 * - PaymentIcon (آیکون تراکنش)
 *
 * توابع اصلی:
 * - `onSubmit`: ارسال درخواست مرحله اول برای بررسی اطلاعات انتقال
 * - `onSubmitStepOneModal`: تأیید جزئیات تراکنش در مودال
 * - `onSubmitOtpModal`: ارسال کد تأیید (OTP) و نهایی‌سازی تراکنش
 * - `toEnglishNumber`: تبدیل اعداد فارسی به انگلیسی برای ارسال صحیح به API
 * - `handleNextStep`: تغییر مرحله فعلی فرآیند
 *
 * مقادیر استیت‌ها:
 * - `step`: مرحله فعلی فرآیند (۰: فرم، ۱: خلاصه تراکنش، ۲: OTP)
 * - `amount`: مبلغ واردشده
 * - `card`: شماره کیف پول مقصد
 * - `payFor`: توضیح تراکنش
 * - `otp`: کد تأیید
 * - `selected`: نوع موجودی انتخاب‌شده (نقدی یا اعتباری)
 *
 * @component
 * @param {Object} props
 * @param {Function} props.setEnableRequest - تابعی برای فعال‌سازی قابلیت ارسال مجدد درخواست پس از موفقیت عملیات
 *
 * @example
 * ```tsx
 * const [enableRequest, setEnableRequest] = useState(false);
 *
 * <ContentBalanceTransfer setEnableRequest={setEnableRequest} />
 * ```
 */

type TransferForm = z.infer<typeof transferSchema>;
const ContentBalanceTransfer = ({ setEnableRequest, }: { setEnableRequest: any; }) => {
  const [step, setStep] = React.useState(0);
  const [amount, setAmount] = React.useState("");
  const [card, setCard] = React.useState("");
  const [payFor, setPayFor] = React.useState("");
  const [detailStepOne, setDetailStepOne] = React.useState<any>();
  const [otp, setOtp] = React.useState("");
  const [selected, setSelected] = useState("all");
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const {
    data: dataBalance,
    isLoading: isLoadingBalance,
    isError: isErrBalance,
  } = useQuery({
    queryKey: ["validation-wallet-balance"],
    queryFn: fetchWalletBalance,
  });
  const { data: dataProfile } = useQuery({
    queryKey: ["BalanceTransfer-data"],
    queryFn: () => fetchProfile(),
  });
  const { register, handleSubmit, formState: { errors } } = useForm<TransferForm>({
    resolver: zodResolver(transferSchema),
  });

  const { mutate: fetchTransactions, data, error, isPending, } = useMutation({
    mutationFn: postPreTransfer,
    onSuccess: (data: any) => {
      setDetailStepOne(data);
      setStep(1);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message);
    },
  });
  const dataOptionSelect = [
    {
      label: `موجودی نقدی(ریال) : ${numberSeparator(dataBalance?.balance)} `,
      value: "all",
    },
    {
      label: ` موجودی اعتباری(ریال): ${numberSeparator(
        dataBalance?.credit?.balance
      )}`,
      value: "cash_20card",
    },
  ];
  // description submit api for first modal
  const { mutate: fetchDetailTransactions } = useMutation({
    mutationFn: postDetailTransfer,
    onSuccess: (data: any) => {
      handleNextStep(2);
      if (setEnableRequest) setEnableRequest(true);
    },
  });
  function toEnglishNumber(str: string) {
    return str.replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString());
  }

  const { mutate: fetchOtpTransactions } = useMutation({
    mutationFn: postOtpTransfer,
    onSuccess: (data: any) => {
      handleNextStep(0);
      toast.success("با موفقیت انجام شد");
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error?.response?.data?.message);
    },
  });
  const handleNextStep = (id: number) => {
    if (id === 1) return;
    setStep(id);
  };
  const onSubmit = (data: any) => {
    const requestBody = {
      amount: toEnglishNumber(amount),
      card: card,
      payFor: toEnglishNumber(payFor),
    };
    fetchTransactions(requestBody);
  };

  //on submit step1 modal
  const onSubmitStepOneModal = () => {
    const requestBody = {
      amount: toEnglishNumber(amount),
      card: toEnglishNumber(card),
      payFor: toEnglishNumber(payFor),
    };
    fetchDetailTransactions(requestBody);
  };

  //on submit Otp modal
  const onSubmitOtpModal = () => {
    const requestBody = {
      amount: amount,
      card: card,
      payFor: payFor,
      otp: otp,
    };
    fetchOtpTransactions(requestBody);
  };
  const masked = useMaskedCardNumber;

  return (
    <StackKit
      direction={isSmall ? "column-reverse" : "row"}
      sx={{
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",
        padding: isSmall ? "10px" : "0 <ImgStaticCard20px",
        gap: isSmall ? 4 : 2,
      }}
    >
      {/* === Form Section === */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          width: isSmall ? "100%" : "55%",
          height: "100%",
        }}
      >
        <StackKit
          direction="column"
          sx={{ width: "100%", height: "auto", background: "linear-gradient(97.77deg, rgba(0, 0, 0, 0.4) -9.73%, rgba(23, 23, 23, 0.281106) 16.04%, rgba(115, 103, 240, 0.25) 50.23%, rgba(65, 65, 65, 0.231942) 87.66%, rgba(0, 0, 0, 0.16) 109.42%)", borderRadius: "8px", padding: isSmall ? "15px" : "20px", gap: isSmall ? 3 : 2, }}
        >
          <TypoGraphyKit variant="body1" textAlign={'start'}>
            جهت انجام فرایند کارت به کارت موارد زیر را تکمیل نمایید.
          </TypoGraphyKit>

          {/* مبلغ */}
          <StackKit direction={{ xs: "column", md: "row" }} sx={{ width: "100%" }} gap={{ xs: 1, sm: 2 }}>
            <TypoGraphyKit
              textAlign={{ xs: "right", sm: "right" }}
              variant="body1"
              sx={{
                width: { xs: "100%", sm: "40%" },
                mb: { xs: 1, sm: 0 }
              }}
            >
              مبلغ مورد نظر جهت انتقال وجه را وارد نمایید.
            </TypoGraphyKit>
            <Box sx={{ width: { xs: "100%", md: "60%" } }}>
              <TextField
                fullWidth
                sx={{
                  bgcolor: "#383838",
                  borderRadius: "10px",
                  '& .MuiInputBase-root': {
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }
                }}
                placeholder={`${numberSeparator(20000000)}`}
                {...register("amount")}
                value={amount ? `${toPersianNumber(numberSeparator(amount))}` : ""}
                error={!!errors.amount}
                onChange={(e) => {
                  const englishNumber = toEnglishNumber(e.target.value);
                  const rawValue = englishNumber.replace(/[^0-9]/g, "");
                  if (Number(rawValue) <= 50000000) {
                    setAmount(rawValue);
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <TypoGraphyKit variant="body2">ریال</TypoGraphyKit>
                    </InputAdornment>
                  ),
                }}
              />
              {errors.amount && (
                <TypoGraphyKit variant="caption" sx={{ color: "#f44336", mt: "4px" }}>
                  {errors.amount.message}
                </TypoGraphyKit>
              )}
            </Box>
          </StackKit>

          {/* شماره کیف پول */}
          <StackKit direction={{ xs: "column", md: "row" }} sx={{ width: "100%" }} gap={{ xs: 1, sm: 2 }}>
            <TypoGraphyKit
              textAlign={{ xs: "right", sm: "right" }}
              variant="body1"
              sx={{
                width: { xs: "100%", sm: "40%" },
                mb: { xs: 1, sm: 0 }
              }}
            >
              شماره کیف پول مقصد را وارد نمایید.
            </TypoGraphyKit>
            <Box sx={{ width: { xs: "100%", md: "60%" } }}>
              <TextField
                fullWidth
                sx={{
                  bgcolor: "#383838",
                  borderRadius: "10px",
                  '& .MuiInputBase-root': {
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }
                }}
                placeholder="---- --- --- --- --- --- ---"
                {...register("card")}
                error={!!errors.card}
                value={card ? `${toPersianNumber(card)}` : ""}
                onChange={(e) => {
                  const englishNumber = toEnglishNumber(e.target.value);
                  const rawValue = englishNumber.replace(/[^0-9]/g, "");
                  if (rawValue.length <= 16) setCard(rawValue);
                }}
              />
              {errors.card && (
                <TypoGraphyKit
                  variant="caption"
                  sx={{ color: "#f44336", mt: "4px" }}
                >
                  {errors.card.message}
                </TypoGraphyKit>
              )}
            </Box>
          </StackKit>

          {/* شرح */}
          <StackKit direction={{ sm: "column", md: "row" }} sx={{ width: "100%" }} gap={{ xs: 1, sm: 2 }}>
            <TypoGraphyKit
              textAlign={{ xs: "right", sm: "right" }}
              variant="body1"
              sx={{
                width: { xs: "100%", sm: "40%" },
                mb: { xs: 1, sm: 0 }
              }}
            >
              شرح
            </TypoGraphyKit>
            <Box sx={{ width: { xs: "100%", md: "60%" } }}>
              <TextField
                fullWidth
                sx={{
                  bgcolor: "#383838",
                  borderRadius: "10px",
                  '& .MuiInputBase-root': {
                    fontSize: { xs: "0.875rem", sm: "1rem" }
                  }
                }}
                placeholder="متن مورد نظر"
                value={payFor}
                {...register("payFor")}
                error={!!errors.payFor}
                onChange={(e) => setPayFor(e.target.value)}
              />
              {errors.payFor && (
                <TypoGraphyKit
                  variant="caption"
                  sx={{ color: "#f44336", mt: "4px" }}
                >
                  {errors.payFor.message}
                </TypoGraphyKit>
              )}
            </Box>
          </StackKit>

          {/* دکمه ادامه */}
          <StackKit
            direction={isSmall ? "column" : "row"}
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: "100%", gap: isSmall ? 2 : 0 }}
          >
            <TypoGraphyKit variant="body2" sx={{ color: "#888" }}>
              سقف انتقال وجه: {numberSeparator(50000000)} ریال
            </TypoGraphyKit>
            <CustomButton
              type="submit"
              sx={{
                width: isSmall ? "100%" : "120px",
                height: "48px",
                mt: isSmall ? 1 : 0,
              }}
              onClick={() => handleNextStep(1)}
            >
              {isPending ? (
                <CircularProgress sx={{ color: "#fff" }} size={24} />
              ) : (
                <TypoGraphyKit variant="body1">ادامه</TypoGraphyKit>
              )}
            </CustomButton>
          </StackKit>
        </StackKit>
      </form>

      {/* === Image + Select Section === */}
      <StackKit direction={'column-reverse'} alignItems="center" sx={{ marginTop: "50px" }}>
        <BoxKit sx={{ minWidth: { sm: "250px", xs: "100%" }, mt: 2, mb: 2 }}>
          <CustomSelectNormal label="" value={selected} onChange={(e) => setSelected(e.target.value)} options={dataOptionSelect ?? []} getOptionLabel={(item) => `${item.label}`} getOptionValue={(item) => item.value} width={"100%"} />
        </BoxKit>
        <BoxKit className="w-[300px] h-[300px]" sx={{ mt: 2 }}>
          <ImgStaticCard imgSlider1="/card-out-simple-flat2-pullipay.svg" imgSlider2="/slide2-pullipay.svg" img1Height="275px" img1Width="150px" img2Height="250px" img2Width="150px"/>
        </BoxKit>
      </StackKit>

      {/* === Step Modals === */}
      <AlertDialog
        paragraph={`شما در حال انتقال وجه از کیف پول خود به کیف پول اعتباری به شماره ${toPersianNumber(
          card
        )} فرد دیگری هستید. جزییات تراکنش به شرح زیر میباشد:`}
        agreeText={"تایید و ارسال درخواست"}
        disagreeText={"باز گشت"}
        onAgree={onSubmitStepOneModal}
        onDisagree={() => handleNextStep(0)}
        isHeader={true}
        header={
          <StackKit direction={"row"} alignItems={"center"} gap={2}>
            <BoxKit>
              <PaymentIcon />
            </BoxKit>
            <TypoGraphyKit variant="body1">انتقال موجودی</TypoGraphyKit>
          </StackKit>
        }
        onClose={() => { }}
        open={step === 1}
      >
        <ContentModal detailStepOne={detailStepOne} />
      </AlertDialog>
      <AlertDialog
        paragraph={`جهت تکمیل فرآیند کد امنیتی وارد شده به شماره تلفن  ${toPersianNumber(
          masked(dataProfile?.phoneNumber)
        )} را وارد نمایید.`}
        agreeText={"ادامه فرایند"}
        disagreeText={"باز گشت"}
        onAgree={onSubmitOtpModal}
        onDisagree={() => handleNextStep(0)}
        isHeader={true}
        header={
          <StackKit direction={"row"} alignItems={"center"} gap={2}>
            <BoxKit>
              <PaymentIcon />
            </BoxKit>
            <TypoGraphyKit variant="body1">انتقال موجودی</TypoGraphyKit>
          </StackKit>
        }
        onClose={() => { }}
        open={step === 2}
      >
        <OtpInput value={otp} onChange={setOtp} onComplete={(val) => { }} hasResend onResend={onSubmitStepOneModal} />
      </AlertDialog>
    </StackKit>
  );
};

export default ContentBalanceTransfer;
